// src/pages/Officer/components/StrategyStatusList.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Grid,
  Button,
  alpha,
  keyframes,
  LinearProgress,
} from '@mui/material';
import {
  ShowChart,
  Circle,
  CheckCircle,
  Schedule,
  KeyboardArrowDown,
  FiberManualRecord,
  Bolt,
  PlayArrow,
} from '@mui/icons-material';

// Glow animation for ready cards
const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px ${alpha('#00ff88', 0.3)}, 0 0 10px ${alpha('#00ff88', 0.2)};
  }
  50% {
    box-shadow: 0 0 20px ${alpha('#00ff88', 0.5)}, 0 0 30px ${alpha('#00ff88', 0.3)}, 0 0 40px ${alpha('#00ff88', 0.2)};
  }
  100% {
    box-shadow: 0 0 5px ${alpha('#00ff88', 0.3)}, 0 0 10px ${alpha('#00ff88', 0.2)};
  }
`;

/**
 * Helper function to safely parse conditions
 */
const parseConditions = (conditions) => {
  if (!conditions) return [];
  
  if (typeof conditions === 'string') {
    try {
      return JSON.parse(conditions);
    } catch (e) {
      return [];
    }
  }
  
  if (Array.isArray(conditions)) {
    return conditions;
  }
  
  return [];
};

/**
 * StrategyCard - Individual strategy status display (CLICKABLE)
 * Changes color when all conditions are fulfilled
 */
const StrategyCard = ({ strategy }) => {
  const navigate = useNavigate();
  
  const conditionStates = strategy.condition_states || {};
  const conditions = parseConditions(strategy.conditions);
  const conditionCount = conditions.filter(c => c.enabled !== false).length;
  
  // Count conditions that are waiting or met
  const waitingCount = Object.values(conditionStates).filter(
    state => state.state?.waiting
  ).length;
  
  const metCount = Object.values(conditionStates).filter(
    state => state.state?.was_met || state.state?.condition_met
  ).length;

  // Determine if ALL conditions are fulfilled (ready for entry)
  const allConditionsFulfilled = conditionCount > 0 && metCount >= conditionCount;
  
  // Calculate completion percentage
  const completionPercentage = conditionCount > 0 ? (metCount / conditionCount) * 100 : 0;

  // Determine status
  const getStatus = () => {
    if (allConditionsFulfilled) {
      return {
        label: 'ALL CONDITIONS MET',
        color: '#00ff88',
        icon: <Bolt sx={{ fontSize: 14 }} />,
        isReady: true,
      };
    }
    if (metCount > 0) {
      return {
        label: `${metCount}/${conditionCount} CONDITIONS`,
        color: '#ffaa00',
        icon: <PlayArrow sx={{ fontSize: 14 }} />,
        isReady: false,
      };
    }
    if (waitingCount > 0) {
      return {
        label: 'WAITING',
        color: '#ffaa00',
        icon: <Schedule sx={{ fontSize: 14 }} />,
        isReady: false,
      };
    }
    if (strategy.signal_count_24h > 0) {
      return {
        label: 'ACTIVE',
        color: '#00d4ff',
        icon: <FiberManualRecord sx={{ fontSize: 14 }} />,
        isReady: false,
      };
    }
    return {
      label: 'MONITORING',
      color: '#9e9e9e',
      icon: <Circle sx={{ fontSize: 14 }} />,
      isReady: false,
    };
  };

  const status = getStatus();

  const handleClick = () => {
    navigate(`/officer/strategies/${strategy.strategy_id}`);
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 2,
        bgcolor: status.isReady 
          ? alpha('#00ff88', 0.08)
          : alpha('#0f1419', 0.6),
        border: '2px solid',
        borderColor: status.isReady
          ? alpha('#00ff88', 0.6)
          : alpha('#666', 0.2),
        borderRadius: 2,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        animation: status.isReady ? `${glowAnimation} 2s ease-in-out infinite` : 'none',
        '&:hover': {
          borderColor: status.isReady
            ? '#00ff88'
            : alpha('#00d4ff', 0.3),
          bgcolor: status.isReady
            ? alpha('#00ff88', 0.12)
            : alpha('#0f1419', 0.8),
          transform: 'translateY(-4px)',
          boxShadow: status.isReady
            ? `0 8px 24px ${alpha('#00ff88', 0.4)}`
            : `0 4px 12px ${alpha('#00d4ff', 0.2)}`,
        },
        // Animated gradient overlay for ready state
        '&::before': status.isReady ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, transparent, ${alpha('#00ff88', 0.15)}, transparent)`,
          animation: 'shimmer 3s infinite',
        } : {},
      }}
    >
      {/* Ready Badge */}
      {status.isReady && (
        <Box
          sx={{
            position: 'absolute',
            top: -1,
            right: -1,
            bgcolor: '#00ff88',
            color: '#000',
            px: 1.5,
            py: 0.5,
            borderBottomLeftRadius: 8,
            fontSize: '0.65rem',
            fontWeight: 900,
            letterSpacing: '0.05em',
            boxShadow: `0 2px 8px ${alpha('#00ff88', 0.5)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Bolt sx={{ fontSize: 12 }} />
          READY
        </Box>
      )}

      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Circle 
                sx={{ 
                  fontSize: 8, 
                  color: status.color,
                  animation: status.isReady ? 'pulse 1s ease-in-out infinite' : 'none'
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: status.isReady ? '#00ff88' : 'grey.100',
                  fontFamily: 'monospace',
                }}
              >
                {strategy.strategy_id}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                {strategy.symbol || 'Loading...'}
              </Typography>
              <Circle sx={{ fontSize: 4, color: 'grey.800' }} />
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                {strategy.timeframe || 'Loading...'}
              </Typography>
            </Stack>
          </Box>

          <Chip
            icon={status.icon}
            label={status.label}
            size="small"
            sx={{
              height: 26,
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              bgcolor: alpha(status.color, status.isReady ? 0.25 : 0.15),
              color: status.color,
              border: '2px solid',
              borderColor: alpha(status.color, status.isReady ? 0.6 : 0.3),
              '& .MuiChip-icon': {
                color: status.color,
              },
            }}
          />
        </Stack>

        {/* Progress Bar - Show when conditions exist */}
        {conditionCount > 0 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: status.isReady ? '#00ff88' : 'grey.600', 
                  fontWeight: 600,
                  fontSize: '0.65rem'
                }}
              >
                CONDITIONS
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: status.isReady ? '#00ff88' : status.color, 
                  fontWeight: 700,
                  fontSize: '0.65rem'
                }}
              >
                {metCount}/{conditionCount}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{
                height: 6,
                borderRadius: 1,
                bgcolor: alpha(status.color, 0.1),
                '& .MuiLinearProgress-bar': {
                  bgcolor: status.isReady ? '#00ff88' : status.color,
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}

        {/* Metrics */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {strategy.signal_count_24h > 0 && (
            <Chip
              label={`${strategy.signal_count_24h} signals (24h)`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: alpha('#00d4ff', 0.1),
                color: '#00d4ff',
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.2),
              }}
            />
          )}
        </Stack>

        {/* Status Message */}
        {status.isReady ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha('#00ff88', 0.15),
              border: '1px solid',
              borderColor: alpha('#00ff88', 0.4),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CheckCircle sx={{ fontSize: 16, color: '#00ff88' }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#00ff88', 
                  fontWeight: 700,
                  textAlign: 'center',
                  fontSize: '0.7rem'
                }}
              >
                All conditions fulfilled - Ready to trade!
              </Typography>
            </Stack>
          </Box>
        ) : (
          metCount > 0 && (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: alpha('#ffaa00', 0.1),
                border: '1px solid',
                borderColor: alpha('#ffaa00', 0.2),
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#ffaa00', 
                  fontWeight: 600,
                  fontSize: '0.65rem'
                }}
              >
                {conditionCount - metCount} more {conditionCount - metCount === 1 ? 'condition' : 'conditions'} needed
              </Typography>
            </Box>
          )
        )}

        {/* Last Update */}
        {strategy.lastUpdate && (
          <Typography
            variant="caption"
            sx={{
              color: 'grey.700',
              fontFamily: 'monospace',
              fontSize: '0.6rem',
            }}
          >
            Last update: {new Date(strategy.lastUpdate).toLocaleTimeString()}
          </Typography>
        )}
      </Stack>

      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </Paper>
  );
};

/**
 * StrategyStatusList - Display all active strategies with pagination
 */
const StrategyStatusList = ({ activeStrategies = {}, isConnected = false }) => {
  const [displayCount, setDisplayCount] = useState(10);

  const strategies = Object.values(activeStrategies);
  
  // Sort strategies by readiness and last update
  // Ready strategies appear first
  const sortedStrategies = [...strategies].sort((a, b) => {
    const aConditionStates = a.condition_states || {};
    const bConditionStates = b.condition_states || {};
    
    // Safely get conditions as arrays
    const aConditions = parseConditions(a.conditions);
    const bConditions = parseConditions(b.conditions);
    
    const aConditionCount = aConditions.filter(c => c.enabled !== false).length;
    const bConditionCount = bConditions.filter(c => c.enabled !== false).length;
    
    const aMetCount = Object.values(aConditionStates).filter(
      state => state.state?.was_met || state.state?.condition_met
    ).length;
    
    const bMetCount = Object.values(bConditionStates).filter(
      state => state.state?.was_met || state.state?.condition_met
    ).length;
    
    const aReady = aConditionCount > 0 && aMetCount >= aConditionCount;
    const bReady = bConditionCount > 0 && bMetCount >= bConditionCount;
    
    // Ready strategies first
    if (aReady && !bReady) return -1;
    if (!aReady && bReady) return 1;
    
    // Then sort by completion percentage
    const aPercentage = aConditionCount > 0 ? (aMetCount / aConditionCount) : 0;
    const bPercentage = bConditionCount > 0 ? (bMetCount / bConditionCount) : 0;
    
    if (aPercentage !== bPercentage) {
      return bPercentage - aPercentage;
    }
    
    // Then sort by last update
    const dateA = new Date(a.lastUpdate || a.last_signal || 0);
    const dateB = new Date(b.lastUpdate || b.last_signal || 0);
    return dateB - dateA;
  });

  const visibleStrategies = sortedStrategies.slice(0, displayCount);
  const hasMore = sortedStrategies.length > displayCount;

  const handleShowMore = () => {
    setDisplayCount(prev => Math.min(prev + 10, sortedStrategies.length));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        bgcolor: alpha('#0a0e14', 0.8),
        border: '1px solid',
        borderColor: alpha('#00d4ff', 0.1),
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: alpha('#00d4ff', 0.1),
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShowChart sx={{ color: '#00d4ff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'grey.50' }}
            >
              Active Strategies
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
              Showing {visibleStrategies.length} of {sortedStrategies.length} • Last 24 hours
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={isConnected ? 'MONITORING' : 'OFFLINE'}
          size="small"
          sx={{
            height: 24,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            bgcolor: isConnected ? alpha('#00ff88', 0.15) : alpha('#666', 0.15),
            color: isConnected ? '#00ff88' : '#9e9e9e',
            border: '1px solid',
            borderColor: isConnected ? alpha('#00ff88', 0.3) : alpha('#666', 0.3),
          }}
        />
      </Stack>

      {/* Strategy Grid */}
      {sortedStrategies.length === 0 ? (
        <Box
          sx={{
            py: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShowChart sx={{ fontSize: 48, color: 'grey.800', mb: 2 }} />
          <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 600 }}>
            No active strategies
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'grey.700', textAlign: 'center', maxWidth: 280, mt: 1 }}
          >
            Configure and activate strategies to see them here
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            {visibleStrategies.map((strategy) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={strategy.strategy_id}>
                <StrategyCard strategy={strategy} />
              </Grid>
            ))}
          </Grid>

          {/* Show More Button */}
          {hasMore && (
            <Button
              onClick={handleShowMore}
              fullWidth
              variant="outlined"
              endIcon={<KeyboardArrowDown />}
              sx={{
                py: 1.5,
                borderColor: alpha('#00d4ff', 0.3),
                color: '#00d4ff',
                fontWeight: 600,
                fontSize: '0.85rem',
                '&:hover': {
                  borderColor: '#00d4ff',
                  bgcolor: alpha('#00d4ff', 0.05),
                },
              }}
            >
              Show More ({sortedStrategies.length - displayCount} remaining)
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default StrategyStatusList;
